import React, { useState, useEffect, useCallback } from 'react';
import monitoringService from '../services/monitoringService';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // all, unread, critical, warning, info
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const result = await monitoringService.getAlerts(filter === 'unread');
      if (result.success) {
        let filtered = result.data.alerts;
        if (['critical', 'warning', 'info'].includes(filter)) {
          filtered = filtered.filter(a => a.severity === filter);
        }
        setAlerts(filtered);
        setUnreadCount(result.data.unreadCount);
      }
    } catch (err) {
      console.error('Alerts load error:', err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleMarkRead = async (id) => {
    await monitoringService.markAlertRead(id);
    loadAlerts();
  };

  const handleMarkAllRead = async () => {
    await monitoringService.markAllRead();
    loadAlerts();
  };

  const getSeverityStyle = (severity) => {
    const map = {
      critical: { bg: 'bg-red-50', border: 'border-red-200', icon: '🔴', label: '심각' },
      high: { bg: 'bg-orange-50', border: 'border-orange-200', icon: '🟠', label: '높음' },
      warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: '🟡', label: '주의' },
      info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: '🔵', label: '정보' }
    };
    return map[severity] || map.info;
  };

  const filters = [
    { id: 'all', label: '전체' },
    { id: 'unread', label: `미읽음 (${unreadCount})` },
    { id: 'critical', label: '심각' },
    { id: 'warning', label: '주의' },
    { id: 'info', label: '정보' }
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        <p className="text-gray-600 text-sm">알림 로딩 중...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-[28px] font-bold text-gray-900 m-0">알림</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">{unreadCount}개의 미읽은 알림</p>
          )}
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} className="text-sm text-blue-500 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">모두 읽음</button>
          )}
          <button onClick={loadAlerts} className="text-sm text-gray-500 hover:text-gray-700 bg-gray-100 px-4 py-2 rounded-lg">새로고침</button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              filter === f.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >{f.label}</button>
        ))}
      </div>

      {/* Alert list */}
      {alerts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <p className="text-4xl mb-4">🔔</p>
          <p className="text-lg">알림이 없습니다</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => {
            const style = getSeverityStyle(alert.severity);
            return (
              <div
                key={alert.id}
                className={`${style.bg} border ${style.border} rounded-xl p-4 flex items-start gap-3 transition-all ${
                  !alert.is_read ? 'ring-2 ring-blue-200' : 'opacity-80'
                }`}
              >
                <span className="text-lg mt-0.5">{style.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 text-sm">{alert.title}</span>
                    {!alert.is_read && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                    )}
                  </div>
                  {alert.message && <p className="text-sm text-gray-600 mb-1">{alert.message}</p>}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {alert.source && <span>{alert.source}</span>}
                    <span>{new Date(alert.created_at).toLocaleString('ko-KR')}</span>
                  </div>
                </div>
                {!alert.is_read && (
                  <button
                    onClick={() => handleMarkRead(alert.id)}
                    className="text-xs text-blue-500 hover:text-blue-700 whitespace-nowrap"
                  >읽음</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Alerts;
